import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { pwa } from '../../lib/pwa';
import { cookieConsent } from '../CookieBanner';
import { PLAY_STORE_URL } from '../../config/landingContent';

const DISMISS_KEY = 'nymia_android_play_banner_until';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const GOLD = '#D4AF37';

const isAndroidBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua) || navigator.userAgentData?.platform === 'Android';
  const isWebView = /; wv\)/.test(ua) || !!window.ReactNativeWebView;
  return isAndroid && !isWebView;
};

const isDismissed = () => {
  try { return Date.now() < Number(window.localStorage.getItem(DISMISS_KEY) || 0); } catch (_e) { return false; }
};

// Homepage-only smart banner: Android browsers, not installed, not dismissed in the last 7 days,
// shown only once the cookie choice has been made so the two cards never overlap.
export const AndroidPlayBanner = () => {
  const { t } = useTranslation('landing');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const eligible = isAndroidBrowser() && !pwa.isStandalone() && !isDismissed();
    if (!eligible) return undefined;
    if (cookieConsent()) { setVisible(true); return undefined; }
    const onConsent = () => setVisible(true);
    window.addEventListener('nymia:cookie-consent', onConsent);
    return () => window.removeEventListener('nymia:cookie-consent', onConsent);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now() + SEVEN_DAYS_MS)); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-3 left-3 right-[4.5rem] z-40 sm:left-4 sm:right-auto sm:max-w-sm"
      role="complementary"
      aria-label="Nymia Android"
      data-testid="android-play-banner"
    >
      <div className="flex items-start gap-3 rounded-2xl border border-[#D4AF37]/35 bg-[#0A0A0A]/95 p-3 shadow-[0_18px_50px_rgba(0,0,0,0.7)] backdrop-blur-xl">
        <img src="/pwa/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <p className="text-[12px] leading-snug text-white/85" data-testid="android-play-banner-text">
            {t('androidBanner.text')}
          </p>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center rounded-full px-3.5 py-1.5 text-[11px] font-bold text-black transition-opacity hover:opacity-90"
            style={{ backgroundColor: GOLD }}
            data-testid="android-play-banner-cta"
          >
            {t('androidBanner.cta')}
          </a>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded-full p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={t('androidBanner.close')}
          data-testid="android-play-banner-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default AndroidPlayBanner;
